import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PortalHeader } from "@/shared/layout/PortalHeader";
import { usePhysicianAuthStore } from "@/stores/physicianAuthStore";
import { getPhysicianMeApi } from "@/portals/physician/api/physicianAuthApi";
import type { PhysicianProfile } from "@/portals/physician/api/types";
import { PhysicianSidebar } from "./PhysicianSidebar";
import { getInitials } from "./getInitials";
import { physicianName } from "@/portals/physician/lib/physicianName";

// Same shell every actor uses (see CaregiverLayout / AdminLayout): shared
// PortalSidebar + PortalHeader, per-route title map, routed <main>.
const pageMeta: Record<string, { title: string; description: string }> = {
  "/physician": { title: "Review Queue", description: "Items assigned to you and waiting in the shared queue" },
  "/physician/my-items": { title: "My Items", description: "Items you claimed or an admin assigned to you" },
};

export function PhysicianLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const signOut = usePhysicianAuthStore((s) => s.signOut);
  const sessionUser = usePhysicianAuthStore((s) => s.user);
  const [me, setMe] = useState<PhysicianProfile | null>(null);
  const meta = location.pathname.startsWith("/physician/review/") || location.pathname.startsWith("/physician/my-items/")
    ? { title: "Review Item", description: "Records, RxNorm matches and the patient's answers" }
    : pageMeta[location.pathname] ?? pageMeta["/physician"];

  useEffect(() => {
    let cancelled = false;
    getPhysicianMeApi().then(({ data }) => {
      if (!cancelled) setMe(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // /me is the source of truth for the name; the login response's user (if the
  // backend sends one) only covers the moment before it resolves or if it fails.
  const profile = me ?? sessionUser;
  const name = profile ? physicianName(profile) : "";

  const handleLogout = () => {
    void signOut();
    navigate("/physician/login", { replace: true });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <PhysicianSidebar name={name} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <PortalHeader
          title={meta.title}
          description={meta.description}
          avatarInitials={getInitials(name)}
          showNotifications
          notificationsVariant="physician"
          menuItems={[{ label: "Logout", onClick: handleLogout, danger: true }]}
        />
        <main id="main-content" className="msq-route-main flex-1 overflow-y-auto bg-[#F9F9F9]">
          {children}
        </main>
      </div>
    </div>
  );
}
